/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useSearchParams } from "react-router";
import { Link } from "../components/ui/Link";
import { ChevronRight, ClipboardList } from "lucide-react";
import { listReferrals, type ReferralListItem } from "../api/referrals";
import { useChrome } from "../contexts/ChromeContext";
import { ReferralStatusBadge } from "../components/ReferralStatusBadge";
import { RegistryDirectory } from "../components/RegistryDirectory";
import { FilterBar } from "../components/FilterBar";
import {
  filtersFromParams,
  filtersToScim,
  paramsWithFilters,
  type ActiveFilter,
} from "../lib/filters";
import { REFERRAL_FILTER_FIELDS } from "../lib/referrals";

/** Every referral, newest first, searchable by family, provider, or contact details. */
export function Referrals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromParams(REFERRAL_FILTER_FIELDS, searchParams);
  const setFilters = (next: ActiveFilter[]) =>
    setSearchParams(
      (previous) => paramsWithFilters(previous, REFERRAL_FILTER_FIELDS, next),
      { replace: true },
    );
  const filter = filtersToScim(REFERRAL_FILTER_FIELDS, filters);
  useChrome({}, []);

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Referrals
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          Families referred to Manatee, from the referring provider form, the
          pediatric screener, and faxes. Open one to see who referred them and
          who was referred.
        </p>
      </header>

      <RegistryDirectory
        countNoun="referral"
        countHint="Referrals recorded since the referral system launched (Oct 2025). Earlier referrals are not in this list."
        placeholder="Search by family, provider, email, or phone"
        load={(q, page) => listReferrals(q, page, filter)}
        filterKey={filter ?? ""}
        filters={
          <FilterBar fields={REFERRAL_FILTER_FIELDS} active={filters} onChange={setFilters} />
        }
        itemKey={(referral) => referral.id}
        renderCard={(referral) => (
          <ReferralCard key={referral.id} referral={referral} />
        )}
        empty={{
          icon: <ClipboardList size={20} />,
          title: "No referrals match",
          description:
            "Try another family, provider, email, or phone, or clear the filters.",
        }}
      />
    </div>
  );
}

function ReferralCard({ referral }: { referral: ReferralListItem }) {
  const clients =
    referral.clientNames.length > 0
      ? referral.clientNames.join(", ")
      : "Unnamed family";
  return (
    <li className="group border border-border bg-bg-elevated shadow-card lift relative flex h-full flex-col gap-3 rounded-3xl p-5">
      <div className="min-w-0">
        <h2 className="truncate font-display text-xl leading-tight text-text-primary">
          <Link
            to={`/referrals/${referral.id}`}
            className="after:absolute after:inset-0 after:rounded-3xl"
          >
            {clients}
          </Link>
        </h2>
        <p className="mt-0.5 truncate text-xs text-text-tertiary">
          {referral.organizationName ?? "Unknown organization"}
          {referral.providerName ? ` · ${referral.providerName}` : ""}
        </p>
      </div>
      <div className="relative z-10 flex w-fit flex-wrap items-center gap-2">
        {referral.status && (
          <ReferralStatusBadge status={referral.status} createdAt={referral.createdAt} />
        )}
      </div>
      <div className="mt-auto flex items-center justify-between gap-3 text-xs text-text-tertiary">
        <span className="whitespace-nowrap">
          {referral.createdAt
            ? new Date(referral.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "—"}
        </span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          Referral {referral.id}
          <ChevronRight
            size={16}
            className="text-text-tertiary transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </li>
  );
}
